import {
  Component,
  Output,
  EventEmitter,
  Input,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { getAudioRecordingFormat } from '../../../../utils/file-processing.utils';
import { isValidAudioRecording } from '../../../../utils/input-handling.utils';

@Component({
  selector: 'app-audio-recorder',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('pulsate', [
      state('pulsate', style({ transform: 'scale(1)' })),
      transition('* => pulsate', [
        animate('0.5s ease-in-out', style({ transform: 'scale(1.1)' })),
        animate('0.5s ease-in-out', style({ transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class AudioRecorderComponent implements OnDestroy {
  @Input() disabled = false;
  @Input() visible = true;
  @Input() preferredFormat = 'webm';
  @Input() minRecordingSize = 3000;

  @Output() recordingStarted = new EventEmitter<void>();
  @Output() recordingStopped = new EventEmitter<void>();
  @Output() audioRecorded = new EventEmitter<Blob>();
  @Output() recordingError = new EventEmitter<string>();

  isRecording = false;
  private mediaRecorder?: MediaRecorder;
  private mediaStream?: MediaStream;
  private audioChunks: Blob[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.cleanup();
  }

  get recordingIcon(): string {
    return this.isRecording ? 'graphic_eq' : 'mic';
  }

  startRecording(): void {
    if (this.isRecording || this.disabled) {
      return;
    }

    this.isRecording = true;
    this.cdr.markForCheck();
    this.recordingStarted.emit();

    const captureFormat = getAudioRecordingFormat(this.preferredFormat);

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.mediaStream = stream;

        this.mediaRecorder = new MediaRecorder(stream, {
          mimeType: captureFormat,
        });
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          this.audioChunks.push(event.data);
        };

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: captureFormat });
          this.handleAudioRecording(audioBlob);
          this.cleanup();
        };

        this.mediaRecorder.start();
      })
      .catch((error) => {
        console.error('Error accessing microphone:', error);
        this.isRecording = false;
        this.cdr.markForCheck();
        this.recordingError.emit('Error accessing microphone');
        this.cleanup();
      });
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.isRecording = false;
      this.cdr.markForCheck();
      this.recordingStopped.emit();
      this.mediaRecorder.stop();
    }
  }

  private handleAudioRecording(audioBlob: Blob): void {
    if (!isValidAudioRecording(audioBlob, this.minRecordingSize)) {
      this.recordingError.emit('Recording too short. Hold to record audio, release to stop.');
      return;
    }

    this.audioRecorded.emit(audioBlob);
  }

  private cleanup(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = undefined;
    }
    this.mediaRecorder = undefined;
    this.audioChunks = [];
  }

  disableRightClick(event: MouseEvent): void {
    event.preventDefault();
  }
}